/**
 * 017: structure copied from
 * vendor/isomorphic/apps/isomorphic/src/app/shared/roles-permissions/users-table/columns.tsx
 *
 * Only the data, columns and labels are ours. The screen-to-template mapping is
 * specs/017-isomorphic-theme-only/contracts/screen-map.md.
 */
import type { ColumnDef } from "@tanstack/react-table";
import { Link } from "react-router-dom";
import { Badge } from "rizzui";
import { PiEyeDuotone as EyeIcon, PiShoppingCartDuotone as OrdersIcon } from "react-icons/pi";
import { Badge as KindBadge, AvatarCard, getStatusBadge } from "@steelyard/theme";
import { formatMoney } from "../../lib/format";
import { basisLabel, customerStatusLabel, type CustomerSummary } from "../../lib/services/customers";
import { RowActions } from "../../components/table/row-actions";
import { customerPath } from "../../lib/paths";

/**
 * Sizes are the template's own values: 300 for the name/customer identity cell, 150 for a short
 * field, 140 for status, 130 for actions. No selection column — no bulk action exists on this list
 * or anywhere in `apps/admin` today.
 */
export const customerColumns: ColumnDef<CustomerSummary>[] = [
  {
    id: "customer",
    accessorKey: "displayName",
    header: "Customer",
    size: 300,
    cell: ({ row }) => (
      <Link to={customerPath(row.original.customerNumber)} className="hover:underline">
        <AvatarCard src="" name={row.original.displayName} />
      </Link>
    ),
  },
  {
    id: "status",
    accessorFn: (customer) => customerStatusLabel(customer.status),
    header: "Status",
    size: 170,
    cell: ({ row }) => getStatusBadge(customerStatusLabel(row.original.status)),
  },
  {
    id: "basis",
    header: "Basis",
    size: 150,
    cell: ({ row }) => (
      <Badge variant="flat" color={row.original.isDefaultTier ? "secondary" : "primary"} rounded="md">
        {basisLabel(row.original)}
      </Badge>
    ),
  },
  {
    id: "creditLimit",
    // The API's decimal parsed to a number so the sort compares amounts; nothing computed.
    accessorFn: (customer) => (customer.creditLimit === null || customer.creditLimit === undefined ? undefined : Number(customer.creditLimit)),
    sortUndefined: "last",
    header: "Credit limit",
    size: 150,
    meta: { align: "right" },
    cell: ({ row }) => (
      <span className="font-semibold tabular-nums text-gray-900">
        {row.original.creditLimit === null || row.original.creditLimit === undefined ? "—" : formatMoney(Number(row.original.creditLimit))}
      </span>
    ),
  },
  {
    id: "outstanding",
    accessorFn: (customer) => Number(customer.outstanding),
    header: "Outstanding",
    size: 200,
    meta: { align: "right" },
    // GAP-7 (003 T035): `overLimit` is the API's own comparison (`CustomerBalance.OverLimit`) —
    // badged here, never recomputed from creditLimit/outstanding (constitution I). Same badge shape
    // as orders/columns.tsx's `shortOnStock`.
    cell: ({ row }) => (
      <span className="flex items-center justify-end gap-2">
        {row.original.overLimit ? (
          <Badge size="sm" variant="flat" color="danger">
            Over limit
          </Badge>
        ) : null}
        <span className="font-semibold tabular-nums text-gray-900">{formatMoney(Number(row.original.outstanding))}</span>
      </span>
    ),
  },
  {
    id: "tallyLedger",
    header: "Tally ledger",
    size: 200,
    cell: ({ row }) =>
      row.original.tallyLedgerLinked ? (
        <KindBadge kind="active">Linked</KindBadge>
      ) : (
        <span className="text-muted-foreground text-xs">Not linked — created on their first order</span>
      ),
  },
  {
    id: "actions",
    header: "",
    size: 110,
    // No delete: no endpoint hard-deletes a customer, and Edit is the same page as View (the
    // detail screen holds the grant/correct/demote forms). The menu's one item goes somewhere else:
    // the order list narrowed to this customer (`orders/List.tsx`'s loader reads `customerId`).
    cell: ({ row }) => (
      <RowActions
        name={row.original.displayName}
        primary={{ label: "View customer", icon: EyeIcon, to: customerPath(row.original.customerNumber) }}
        more={[{ label: "Orders from this customer", icon: OrdersIcon, to: `/orders?customerId=${row.original.customerId}` }]}
      />
    ),
  },
];
